import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ActionIcon, TextInput } from '@mantine/core';
import { IconCalendar } from '@tabler/icons-react';

/**
 * Поле даты: ввод вручную в формате ДД.ММ.ГГГГ (01.01.0001) + календарь по
 * иконке (нативный пикер браузера). Значение наружу/внутрь — ISO `YYYY-MM-DD`
 * (как у нативного `type="date"`), поэтому логика форм не меняется.
 *
 * Без зависимости `@mantine/dates` — её нельзя поставить в офлайн-окружении.
 */

function isoToRu(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}
/** Маска ввода: цифры → ДД.ММ.ГГГГ (18052026 → 18.05.2026). */
function maskRu(raw: string): string {
  const dgt = raw.replace(/\D/g, '').slice(0, 8);
  let out = dgt.slice(0, 2);
  if (dgt.length > 2) out += '.' + dgt.slice(2, 4);
  if (dgt.length > 4) out += '.' + dgt.slice(4, 8);
  return out;
}
function ruToIso(s: string): string | null {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const dd = +m[1];
  const mm = +m[2];
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

interface Props {
  label?: ReactNode;
  description?: ReactNode;
  /** ISO `YYYY-MM-DD` или ''. */
  value: string;
  onChange: (iso: string) => void;
  error?: ReactNode;
  size?: string;
}

export function DateField({ label, description, value, onChange, error, size }: Props) {
  const [text, setText] = useState(isoToRu(value));
  const nativeRef = useRef<HTMLInputElement>(null);

  // Синхронизация при внешнем изменении (выбор в календаре, загрузка формы).
  useEffect(() => {
    setText(isoToRu(value));
  }, [value]);

  return (
    <div style={{ position: 'relative' }}>
      <TextInput
        label={label}
        description={description}
        placeholder="ДД.ММ.ГГГГ"
        size={size}
        value={text}
        error={error}
        onChange={(e) => {
          const masked = maskRu(e.currentTarget.value);
          setText(masked);
          if (masked === '') {
            onChange('');
            return;
          }
          const iso = ruToIso(masked);
          if (iso) onChange(iso);
        }}
        onBlur={() => setText(isoToRu(value))}
        rightSection={
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Открыть календарь"
            onClick={() => nativeRef.current?.showPicker?.()}
          >
            <IconCalendar size={16} />
          </ActionIcon>
        }
      />
      {/* Нативный date-input — только для календаря (showPicker). */}
      <input
        ref={nativeRef}
        type="date"
        value={value}
        tabIndex={-1}
        aria-hidden
        onChange={(e) => onChange(e.currentTarget.value)}
        style={{
          position: 'absolute',
          right: 8,
          bottom: 6,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
          border: 0,
          padding: 0,
        }}
      />
    </div>
  );
}
